import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { BuildingFactory, BuildingConfig, BuildingMeshes } from './BuildingFactory';
import { PhysicsWorld } from '../physics/PhysicsWorld';

export interface CityConfig {
  gridSize: number; // Number of blocks in each direction
  blockSize: number; // Size of each block in world units
  roadWidth: number; // Width of roads between blocks
  minBuildingHeight: number;
  maxBuildingHeight: number;
  density: number; // 0-1, probability of building placement
  centerClearRadius: number; // Keep center area clear for player spawn
}

interface CityBlock {
  position: THREE.Vector3;
  buildings: BuildingMeshes[];
  bodies: CANNON.Body[];
}

export class CityGenerator {
  private scene: THREE.Scene;
  private physicsWorld: PhysicsWorld;
  private buildingFactory: BuildingFactory;
  private config: CityConfig;
  private blocks: CityBlock[] = [];
  private mergedMesh: THREE.Mesh | null = null;

  constructor(
    scene: THREE.Scene,
    physicsWorld: PhysicsWorld,
    config?: Partial<CityConfig>
  ) {
    this.scene = scene;
    this.physicsWorld = physicsWorld;
    this.buildingFactory = new BuildingFactory(scene);

    this.config = {
      gridSize: 10, // Expanded for 1000x1000 map
      blockSize: 45,
      roadWidth: 15,
      minBuildingHeight: 8,
      maxBuildingHeight: 50, // Taller buildings for larger city
      density: 0.65,
      centerClearRadius: 60, // Larger clear area for highways
      ...config,
    };
  }

  public generate(): void {
    const { gridSize, blockSize, roadWidth, centerClearRadius } = this.config;
    const totalBlockSize = blockSize + roadWidth;
    const offset = ((gridSize - 1) * totalBlockSize) / 2;

    // Generate city blocks in a grid
    for (let x = 0; x < gridSize; x++) {
      for (let z = 0; z < gridSize; z++) {
        const blockX = x * totalBlockSize - offset;
        const blockZ = z * totalBlockSize - offset;

        // Skip center area for player spawn
        const distFromCenter = Math.sqrt(blockX * blockX + blockZ * blockZ);
        if (distFromCenter < centerClearRadius) {
          continue;
        }

        const block = this.generateBlock(new THREE.Vector3(blockX, 0, blockZ));
        this.blocks.push(block);
      }
    }

    // Create roads
    this.createRoads();

    // Merge geometries for performance (optional - can be enabled for large cities)
    // this.mergeGeometries();
  }

  private generateBlock(centerPos: THREE.Vector3): CityBlock {
    const { blockSize, minBuildingHeight, maxBuildingHeight, density } = this.config;
    const buildings: BuildingMeshes[] = [];
    const bodies: CANNON.Body[] = [];

    // Subdivide block into building lots
    const lotSize = blockSize / 2;
    const lotPositions = [
      new THREE.Vector3(-lotSize / 2, 0, -lotSize / 2),
      new THREE.Vector3(lotSize / 2, 0, -lotSize / 2),
      new THREE.Vector3(-lotSize / 2, 0, lotSize / 2),
      new THREE.Vector3(lotSize / 2, 0, lotSize / 2),
    ];

    for (const lotOffset of lotPositions) {
      // Random chance to skip (empty lot)
      if (Math.random() > density) continue;

      const position = centerPos.clone().add(lotOffset);

      // Randomize building dimensions
      const width = 5 + Math.random() * (lotSize - 8);
      const depth = 5 + Math.random() * (lotSize - 8);
      const height = minBuildingHeight + Math.random() * (maxBuildingHeight - minBuildingHeight);

      // Choose building style based on height
      let style: 'standard' | 'tower' | 'warehouse' | 'skyscraper' = 'standard';
      if (height > 30) {
        style = Math.random() > 0.5 ? 'skyscraper' : 'tower';
      } else if (height < 12) {
        style = 'warehouse';
      }

      const buildingConfig: BuildingConfig = {
        width,
        depth,
        height,
        position,
        style,
      };

      const meshes = this.buildingFactory.createBuilding(buildingConfig);
      this.buildingFactory.addToScene(meshes);
      buildings.push(meshes);

      // Create physics body for collision
      const body = this.createBuildingBody(position, width, height, depth);
      this.physicsWorld.addBody(body);
      bodies.push(body);
    }

    return {
      position: centerPos,
      buildings,
      bodies,
    };
  }

  private createBuildingBody(
    position: THREE.Vector3,
    width: number,
    height: number,
    depth: number
  ): CANNON.Body {
    const shape = new CANNON.Box(new CANNON.Vec3(width / 2, height / 2, depth / 2));
    const body = new CANNON.Body({
      mass: 0, // Static
      shape,
      position: new CANNON.Vec3(position.x, height / 2, position.z),
    });
    body.collisionFilterGroup = 16; // Buildings group (separate from ground)
    body.collisionFilterMask = 2 | 4; // Collide with player and projectiles
    return body;
  }

  private createRoads(): void {
    const { gridSize, blockSize, roadWidth } = this.config;
    const totalBlockSize = blockSize + roadWidth;
    const totalSize = gridSize * totalBlockSize;
    const offset = totalSize / 2;

    // Road material
    const roadMaterial = new THREE.MeshStandardMaterial({
      color: 0x1a1a1a,
      metalness: 0.3,
      roughness: 0.8,
    });

    // Create horizontal roads
    for (let i = 0; i <= gridSize; i++) {
      const roadZ = i * totalBlockSize - offset - roadWidth / 2;

      const roadGeom = new THREE.PlaneGeometry(totalSize + roadWidth, roadWidth);
      const road = new THREE.Mesh(roadGeom, roadMaterial);
      road.rotation.x = -Math.PI / 2;
      road.position.set(0, 0.01, roadZ);
      this.scene.add(road);

      // Road markings
      this.addRoadMarkings(road.position, totalSize + roadWidth, roadWidth, true);
    }

    // Create vertical roads
    for (let i = 0; i <= gridSize; i++) {
      const roadX = i * totalBlockSize - offset - roadWidth / 2;

      const roadGeom = new THREE.PlaneGeometry(roadWidth, totalSize + roadWidth);
      const road = new THREE.Mesh(roadGeom, roadMaterial);
      road.rotation.x = -Math.PI / 2;
      road.position.set(roadX, 0.02, 0);
      this.scene.add(road);

      // Road markings
      this.addRoadMarkings(new THREE.Vector3(roadX, 0, 0), roadWidth, totalSize + roadWidth, false);
    }
  }

  private addRoadMarkings(
    position: THREE.Vector3,
    width: number,
    length: number,
    horizontal: boolean
  ): void {
    const markingMaterial = new THREE.MeshStandardMaterial({
      color: 0xffff00,
      emissive: 0xffff00,
      emissiveIntensity: 0.3,
    });

    // Center line (dashed)
    const dashCount = Math.floor((horizontal ? width : length) / 8);
    for (let i = 0; i < dashCount; i++) {
      const dashGeom = new THREE.PlaneGeometry(horizontal ? 3 : 0.2, horizontal ? 0.2 : 3);
      const dash = new THREE.Mesh(dashGeom, markingMaterial);
      dash.rotation.x = -Math.PI / 2;

      if (horizontal) {
        dash.position.set(
          position.x - width / 2 + (i + 0.5) * (width / dashCount),
          0.03,
          position.z
        );
      } else {
        dash.position.set(
          position.x,
          0.03,
          position.z - length / 2 + (i + 0.5) * (length / dashCount)
        );
      }

      this.scene.add(dash);
    }

    // Edge lines (solid, with neon glow)
    const edgeMaterial = new THREE.MeshStandardMaterial({
      color: 0x00ffff,
      emissive: 0x00ffff,
      emissiveIntensity: 0.5,
    });

    const edgeWidth = 0.15;
    const edgeGeom = new THREE.PlaneGeometry(
      horizontal ? width : edgeWidth,
      horizontal ? edgeWidth : length
    );

    // Create edge lines on both sides of road
    const offset = (horizontal ? length : width) / 2 - 0.5;

    const edge1 = new THREE.Mesh(edgeGeom, edgeMaterial);
    edge1.rotation.x = -Math.PI / 2;
    if (horizontal) {
      edge1.position.set(position.x, 0.03, position.z - offset);
    } else {
      edge1.position.set(position.x - offset, 0.03, position.z);
    }
    this.scene.add(edge1);

    const edge2 = new THREE.Mesh(edgeGeom, edgeMaterial.clone());
    edge2.rotation.x = -Math.PI / 2;
    if (horizontal) {
      edge2.position.set(position.x, 0.03, position.z + offset);
    } else {
      edge2.position.set(position.x + offset, 0.03, position.z);
    }
    this.scene.add(edge2);
  }

  public mergeGeometries(): void {
    // Collect all building geometries for merging
    const geometries: THREE.BufferGeometry[] = [];

    for (const block of this.blocks) {
      for (const building of block.buildings) {
        if (building.building.visible) {
          const geom = building.building.geometry.clone();
          geom.applyMatrix4(building.building.matrixWorld);
          geometries.push(geom);
        }
      }
    }

    if (geometries.length === 0) return;

    // Merge all geometries
    // Note: BufferGeometryUtils.mergeGeometries would be used here
    // For now, we skip actual merging as it requires importing the utility
    console.log(`Would merge ${geometries.length} building geometries`);
  }

  public getBlocks(): CityBlock[] {
    return this.blocks;
  }

  public getBuildingPositions(): THREE.Vector3[] {
    const positions: THREE.Vector3[] = [];
    for (const block of this.blocks) {
      for (const building of block.buildings) {
        positions.push(building.building.position.clone());
      }
    }
    return positions;
  }

  public destroy(): void {
    for (const block of this.blocks) {
      for (const building of block.buildings) {
        this.scene.remove(building.building);
        this.scene.remove(building.edges);
        this.scene.remove(building.details);
      }
      for (const body of block.bodies) {
        this.physicsWorld.removeBody(body);
      }
    }

    if (this.mergedMesh) {
      this.scene.remove(this.mergedMesh);
    }

    this.blocks = [];
  }
}
